// SUB-OFFICE 7 — FORM 12-K, CEREMONIAL ARMATURE ALIGNMENT. Three dials off a
// hidden zero; you sweep them toward a target you cannot read, spending a fixed
// ration of TEST blanks to see how wrong you still are (skew = rotation,
// ghosting = drop force, blotch = ink). Value space is % error from 0. The
// client renders test stamps by replaying its own events with replayDials12K —
// the SAME machine the server runs — and records ONLY hold/release/test/certify
// timestamps. End-values are never client-asserted.

import {
  DIAL_NAMES_12K, replayDials12K, type DialEvent12K, type Form12KSpec,
} from '../../shared/physspec';
import { printLayer } from '../ui/chrome';
import { PhysLogRecorder } from './log';
import type { BenchDeps } from './bench';
import type { BehaviorLog } from '../../shared/physlog';

export function runArmature(
  deps: BenchDeps,
  taskId: string,
  spec: Form12KSpec
): Promise<BehaviorLog | null> {
  return new Promise((resolve) => {
    const rec = new PhysLogRecorder('FORM12K', taskId);
    const layer = printLayer();
    const simEvents: DialEvent12K[] = [];
    const emit = (e: DialEvent12K['e'], dial?: number, dir?: number): void => {
      const t = rec.now();
      simEvents.push({ t, e, ...(dial !== undefined ? { dial } : {}), ...(dir !== undefined ? { dir } : {}) });
      rec.ev(e, { ...(dial !== undefined ? { dial } : {}), ...(dir !== undefined ? { dir } : {}) });
    };

    const card = document.createElement('div');
    card.className = 'paper-modal wide';
    card.innerHTML =
      `<div class="paper-head letterhead"><span>FORM 12-K — CEREMONIAL ARMATURE ALIGNMENT</span>` +
      `<span class="tab-num" id="ar-blanks">BLANKS ${spec.blanks}</span></div>`;
    const body = document.createElement('div');
    body.className = 'paper-body typewriter';
    card.appendChild(body);
    const note = document.createElement('div');
    note.className = 'compose-note';
    note.textContent = 'SWEEP EACH DIAL TO ITS HIDDEN ZERO. SPEND A TEST BLANK TO READ YOUR ERROR. CERTIFY WHEN TRUE.';
    body.appendChild(note);

    const row = document.createElement('div');
    row.style.cssText = 'display:flex;gap:16px;justify-content:center;flex-wrap:wrap;margin:14px 0';
    body.appendChild(row);

    const stampZone = document.createElement('div');
    stampZone.style.cssText = 'min-height:96px;display:flex;gap:10px;justify-content:center;align-items:center;flex-wrap:wrap';
    body.appendChild(stampZone);

    const foot = document.createElement('div');
    foot.className = 'paper-foot';
    const testBtn = document.createElement('button');
    testBtn.type = 'button';
    testBtn.className = 'slam-btn secondary';
    testBtn.textContent = 'TEST BLANK';
    const certifyBtn = document.createElement('button');
    certifyBtn.type = 'button';
    certifyBtn.className = 'slam-btn';
    certifyBtn.textContent = 'CERTIFY';
    const abandonBtn = document.createElement('button');
    abandonBtn.type = 'button';
    abandonBtn.className = 'slam-btn secondary';
    abandonBtn.textContent = 'DISMOUNT';
    foot.append(abandonBtn, testBtn, certifyBtn);
    card.appendChild(foot);
    layer.appendChild(card);

    const blanksEl = card.querySelector('#ar-blanks') as HTMLElement;
    let over = false;
    let testsUsed = 0;
    const holding = new Array(spec.dial_count).fill(false);

    for (let d = 0; d < spec.dial_count; d++) {
      const col = document.createElement('div');
      col.style.cssText = 'display:flex;flex-direction:column;align-items:center;gap:6px;min-width:120px';
      const name = document.createElement('div');
      name.className = 'field-label';
      name.textContent = DIAL_NAMES_12K[d] || `DIAL ${d + 1}`;
      col.appendChild(name);
      const up = mkHold('SWEEP +', d, 1);
      const down = mkHold('SWEEP −', d, -1);
      col.append(up, down);
      row.appendChild(col);
    }

    function mkHold(label: string, dial: number, dir: number): HTMLButtonElement {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'slam-btn' + (dir < 0 ? ' secondary' : '');
      b.style.cssText = 'font-size:10px;min-width:96px;touch-action:none';
      b.textContent = label;
      b.addEventListener('pointerdown', (e) => {
        if (over || holding[dial]) return;
        e.preventDefault();
        b.setPointerCapture(e.pointerId);
        holding[dial] = true;
        emit('dial_hold', dial, dir);
      });
      const release = (): void => {
        if (!holding[dial]) return;
        holding[dial] = false;
        emit('dial_release', dial);
      };
      b.addEventListener('pointerup', release);
      b.addEventListener('pointercancel', release);
      return b;
    }

    const renderTest = (): void => {
      // ensure any live holds are settled for a clean read (mirror server)
      const replay = replayDials12K(spec, simEvents);
      const errs = replay.test_errors.length ? replay.test_errors[replay.test_errors.length - 1] : replay.final_pct;
      stampZone.innerHTML = '';
      errs.forEach((err, d) => {
        const s = document.createElement('div');
        const mag = Math.min(1, Math.abs(err) / 30);
        const box = 'width:70px;height:70px;border:2px solid var(--carbon);display:flex;align-items:center;justify-content:center;font-size:9px;text-align:center;background:var(--paper)';
        // distortion per dial: 0 skew, 1 ghost, 2 blotch
        let transform = '';
        let filter = '';
        let shadow = '';
        if (d === 0) transform = `skewX(${(err * 0.8).toFixed(1)}deg) rotate(${(err * 0.3).toFixed(1)}deg)`;
        else if (d === 1) shadow = `box-shadow:${(err * 0.2).toFixed(1)}px ${(mag * 3).toFixed(1)}px 0 rgba(28,26,23,${(mag * 0.6).toFixed(2)})`;
        else filter = `filter:blur(${(mag * 2).toFixed(1)}px)`;
        s.style.cssText = `${box};transform:${transform};${shadow};${filter}`;
        s.innerHTML = `${DIAL_NAMES_12K[d] || d}<br>${err >= 0 ? '+' : ''}${err.toFixed(1)}%`;
        stampZone.appendChild(s);
      });
    };

    const finishOk = (): void => {
      if (over) return;
      over = true;
      window.removeEventListener('keydown', onKey);
      card.remove();
      const built = rec.build(false);
      if (built.valid) resolve(built.log);
      else {
        deps.bus.emit('toast', `The alignment log did not assemble (${built.reason}).`);
        resolve(null);
      }
    };
    const abandon = (): void => {
      if (over) return;
      over = true;
      window.removeEventListener('keydown', onKey);
      emit('dismount_abandoned');
      card.remove();
      resolve(null);
    };
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') abandon(); };
    window.addEventListener('keydown', onKey);

    testBtn.addEventListener('click', () => {
      if (over || testsUsed >= spec.blanks) return;
      // settle live holds first so the blank reads a consistent state
      for (let d = 0; d < spec.dial_count; d++) if (holding[d]) { holding[d] = false; emit('dial_release', d); }
      testsUsed++;
      emit('test_slam', testsUsed - 1);
      deps.bus.emit('audio:thunk');
      blanksEl.textContent = `BLANKS ${spec.blanks - testsUsed}`;
      if (testsUsed >= spec.blanks) { testBtn.disabled = true; testBtn.style.opacity = '.4'; }
      renderTest();
    });
    certifyBtn.addEventListener('click', () => {
      if (over) return;
      for (let d = 0; d < spec.dial_count; d++) if (holding[d]) { holding[d] = false; emit('dial_release', d); }
      emit('certify');
      deps.bus.emit('audio:thunk');
      deps.bus.emit('chyron', 'THE ARMATURE IS CERTIFIED. THE NEXT VERDICT WILL WEAR WHATEVER SKEW YOU LEFT.');
      finishOk();
    });
    abandonBtn.addEventListener('click', abandon);

    emit('mounted');
    deps.bus.emit('chyron', 'ARMATURE ALIGNMENT. THE STAMP MUST STRIKE TRUE, OR EVERY VERDICT AFTER LEANS.');
  });
}
